import { useState, useEffect } from 'react';
import { Save, Settings, DollarSign, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const SettingsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: 'Phantom Trades',
    maintenance_mode: false,
    registration_enabled: true,
    trading_enabled: true,
    deposits_enabled: true,
    withdrawals_enabled: true,
    kyc_required: false,
    default_currency: 'USD',
    default_timezone: 'UTC'
  });

  const [tradingConfig, setTradingConfig] = useState({
    binary_trading_enabled: true,
    ai_arbitrage_enabled: true,
    binary_durations: [30, 60, 120, 300, 600, 900],
    default_profit_rate: 0.85
  });

  // Reconciliation state
  const [reconPreview, setReconPreview] = useState(null);
  const [reconRunning, setReconRunning] = useState(false);
  const [reconStatus, setReconStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchReconStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [platform, trading] = await Promise.all([
        api.get('/admin/settings', { headers: { 'Authorization': `Bearer ${token}` }}).catch(() => ({ data: {} })),
        api.get('/admin/settings/trading', { headers: { 'Authorization': `Bearer ${token}` }}).catch(() => ({ data: {} }))
      ]);
      
      if (Object.keys(platform.data).length > 0) {
        setPlatformSettings(prev => ({ ...prev, ...platform.data }));
      }
      if (Object.keys(trading.data).length > 0) {
        setTradingConfig(prev => ({ ...prev, ...trading.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSavePlatformSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      await api.put('/admin/settings', platformSettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Platform settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTradingConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      await api.put('/admin/settings/trading', tradingConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Trading configuration saved successfully');
    } catch (error) {
      console.error('Error saving trading config:', error);
      toast.error('Failed to save trading configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchReconStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/admin/wallet/reconciliation/status', { headers: { Authorization: `Bearer ${token}` } });
      setReconStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch reconciliation status:', err);
    }
  };

  const handleReconPreview = async () => {
    try {
      setReconRunning(true);
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/admin/wallet/reconciliation/preview', { headers: { Authorization: `Bearer ${token}` } });
      setReconPreview(res.data);
    } catch {
      toast.error('Failed to preview reconciliation');
    } finally {
      setReconRunning(false);
    }
  };

  const handleReconRun = async () => {
    if (!window.confirm('This will fix all orphan locked balances. Continue?')) return;
    try {
      setReconRunning(true);
      const token = localStorage.getItem('adminToken');
      const res = await api.post('/admin/wallet/reconciliation/run', {}, { headers: { Authorization: `Bearer ${token}` } });
      setReconPreview(res.data);
      toast.success(`Reconciliation complete! ${res.data.users_with_orphans} users fixed, $${res.data.total_orphan_amount_usdt?.toLocaleString()} freed`);
    } catch {
      toast.error('Failed to run reconciliation');
    } finally {
      setReconRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
        <p className="text-gray-400 text-sm">Configure platform and trading parameters</p>
      </div>

      {/* Wallet Reconciliation */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="wallet-reconciliation">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
          <ShieldCheck className="h-5 w-5 text-green-400" />
          Wallet Reconciliation
        </h2>
        <p className="text-gray-400 text-sm mb-4">
          Scans all user wallets for orphan locked funds (from expired/settled trades that failed to unlock) and fixes them.
        </p>

        <div className="flex gap-3 mb-4">
          <button
            onClick={handleReconPreview}
            disabled={reconRunning}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="recon-preview-btn"
          >
            <RefreshCw className={`h-4 w-4 ${reconRunning ? 'animate-spin' : ''}`} />
            {reconRunning ? 'Scanning...' : 'Preview Scan'}
          </button>
          <button
            onClick={handleReconRun}
            disabled={reconRunning || !reconPreview || reconPreview.users_with_orphans === 0}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="recon-run-btn"
          >
            <ShieldCheck className="h-4 w-4" />
            Fix All Orphan Locks
          </button>
          <button
            onClick={async () => {
              try {
                setReconRunning(true);
                const token = localStorage.getItem('adminToken');
                await api.post('/admin/wallet/reconciliation/trigger', {}, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Reconciliation job triggered!');
                setTimeout(fetchReconStatus, 6000);
              } catch { toast.error('Failed to trigger'); }
              finally { setReconRunning(false); }
            }}
            disabled={reconRunning}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="recon-trigger-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Run Full Job Now
          </button>
        </div>

        {/* Scheduler Status */}
        {reconStatus && (
          <div className="bg-gray-800/30 rounded-lg p-3 mb-4 flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-gray-500">Scheduler: </span>
              <span className={reconStatus.scheduler_running ? 'text-green-400' : 'text-red-400'}>
                {reconStatus.scheduler_running ? 'Active' : 'Stopped'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Next auto-run: </span>
              <span className="text-white">{reconStatus.next_scheduled_run ? new Date(reconStatus.next_scheduled_run).toLocaleString() : 'N/A'}</span>
            </div>
            {reconStatus.last_run && (
              <>
                <div>
                  <span className="text-gray-500">Last run: </span>
                  <span className="text-white">{new Date(reconStatus.last_run).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Settled: </span>
                  <span className="text-cyan-400">{reconStatus.settled_trades} trades</span>
                </div>
                <div>
                  <span className="text-gray-500">Fixed: </span>
                  <span className="text-yellow-400">{reconStatus.users_fixed} users</span>
                </div>
              </>
            )}
          </div>
        )}

        {reconPreview && (
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-gray-400 text-xs">Users Scanned</p>
                <p className="text-white font-bold text-lg">{reconPreview.total_users_scanned}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-xs">With Orphans</p>
                <p className={`font-bold text-lg ${reconPreview.users_with_orphans > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {reconPreview.users_with_orphans}
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-xs">Orphan Amount</p>
                <p className={`font-bold text-lg ${reconPreview.total_orphan_amount_usdt > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  ${reconPreview.total_orphan_amount_usdt?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-xs">Stale Lock Records</p>
                <p className="text-white font-bold text-lg">{reconPreview.total_orphan_lock_records_cleaned}</p>
              </div>
            </div>

            {reconPreview.affected_users?.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-gray-300 text-sm font-medium">Affected Users:</p>
                {reconPreview.affected_users.map((u, i) => (
                  <div key={i} className="bg-gray-900/50 rounded p-2 text-sm">
                    <p className="text-gray-300 font-mono text-xs">{u.user_id}</p>
                    {u.fixes?.map((f, j) => (
                      <div key={j} className="flex items-center gap-2 mt-1">
                        <AlertTriangle className="h-3 w-3 text-yellow-400" />
                        <span className="text-yellow-300">{f.crypto}:</span>
                        <span className="text-gray-400">locked={f.wallet_locked}</span>
                        <span className="text-gray-500">&rarr;</span>
                        <span className="text-green-400">{f.actual_locked}</span>
                        <span className="text-red-400 text-xs">(orphan: {f.orphan_amount})</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {reconPreview.users_with_orphans === 0 && (
              <p className="text-green-400 text-center py-2 font-medium">All wallets are clean!</p>
            )}
          </div>
        )}
      </div>

      {/* Platform Settings */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white flex items-center">
            <Settings className="h-5 w-5 mr-2 text-purple-400" />
            Platform Configuration
          </h2>
          <button
            onClick={handleSavePlatformSettings}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>

        <div className="space-y-4">
          {/* Platform Name */}
          <div>
            <label className="text-white font-semibold mb-2 block">Platform Name</label>
            <input
              type="text"
              value={platformSettings.platform_name}
              onChange={(e) => setPlatformSettings({ ...platformSettings, platform_name: e.target.value })}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Toggle Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'maintenance_mode', label: 'Maintenance Mode', color: 'red' },
              { key: 'registration_enabled', label: 'User Registration', color: 'green' },
              { key: 'trading_enabled', label: 'Trading Enabled', color: 'green' },
              { key: 'deposits_enabled', label: 'Deposits Enabled', color: 'green' },
              { key: 'withdrawals_enabled', label: 'Withdrawals Enabled', color: 'green' },
              { key: 'kyc_required', label: 'KYC Required', color: 'yellow' }
            ].map(setting => (
              <div key={setting.key} className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold">{setting.label}</span>
                  <button
                    onClick={() => setPlatformSettings({
                      ...platformSettings,
                      [setting.key]: !platformSettings[setting.key]
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      platformSettings[setting.key]
                        ? `bg-${setting.color}-600`
                        : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        platformSettings[setting.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Currency & Timezone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white font-semibold mb-2 block">Default Currency</label>
              <select
                value={platformSettings.default_currency}
                onChange={(e) => setPlatformSettings({ ...platformSettings, default_currency: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="text-white font-semibold mb-2 block">Default Timezone</label>
              <select
                value={platformSettings.default_timezone}
                onChange={(e) => setPlatformSettings({ ...platformSettings, default_timezone: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="UTC">UTC</option>
                <option value="EST">EST</option>
                <option value="PST">PST</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Trading Configuration */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-green-400" />
            Trading Configuration
          </h2>
          <button
            onClick={handleSaveTradingConfig}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>

        <div className="space-y-4">
          {/* Trading Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">Binary Trading</span>
                <button
                  onClick={() => setTradingConfig({
                    ...tradingConfig,
                    binary_trading_enabled: !tradingConfig.binary_trading_enabled
                  })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    tradingConfig.binary_trading_enabled ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      tradingConfig.binary_trading_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">AI Arbitrage</span>
                <button
                  onClick={() => setTradingConfig({
                    ...tradingConfig,
                    ai_arbitrage_enabled: !tradingConfig.ai_arbitrage_enabled
                  })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    tradingConfig.ai_arbitrage_enabled ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      tradingConfig.ai_arbitrage_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Profit Rate */}
          <div>
            <label className="text-white font-semibold mb-2 block">Default Profit Rate (Binary Trading)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={tradingConfig.default_profit_rate}
              onChange={(e) => setTradingConfig({ ...tradingConfig, default_profit_rate: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-gray-400 text-sm mt-1">Example: 0.85 = 85% profit rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;